import {
  Backdrop,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Typography,
  Box,
} from "@mui/material";
import { makeStyles, createStyles } from "@mui/styles";
// eslint-disable-next-line import/named
import { Theme, useTheme } from "@mui/material/styles";
import React, { useEffect, useState } from "react";
import FolderIcon from "@mui/icons-material/Folder";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import {
  IWikiProduct,
  IWikiPage,
  getWikiListing,
  getWikiContent,
  IWikiFileData,
} from "../../api/wikiApi";
import { getMyProducts, IProductInfo } from "../../api/Account/myProductsApi";
import { IUserV0, getUsersList } from "../../api/Account/usersApi";
import WikiRenderer from "./WikiRenderer";
import { useSelector } from "react-redux";
import { ISession, ILoggedUser } from "../../types/Auth";
import { ToastContainer, toast } from "react-toastify";
import { RootState } from "../../redux/reducers/rootReducer";
import { docsResources } from "./.resources";
import { useResources } from "../../hooks/useResources";
import UserDropDown from "../admin/_shared/UsersDropDown";

const Wiki: React.FC = () => {
  const theme = useTheme();
  const [selectedItem, setSelectedItem] = useState<IWikiPage>();
  const [products, setProducts] = useState<IWikiProduct[]>([]);
  const [content, setContent] = useState<IWikiFileData[]>([]);
  const [selectedContent, setSelectedContent] = useState<IWikiFileData>();
  const [isLoadingPage, setIsLoadingPage] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [users, setUsers] = useState<IUserV0[]>([]);
  const [viewAsUser, setViewAsUser] = useState<string>();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  // Language sync is handled globally in App.tsx
  const res = useResources(docsResources);
  
  const language: string = useSelector((state: RootState) => {
    return state?.language || "en";
  });

  const loggedUser: ILoggedUser = useSelector((state: RootState) => {
    return state.userInfo;
  });

  const session: ISession = useSelector((state: RootState) => {
    return state.auth;
  }); //REDUX

  const showError = (err: string | undefined) => {
    if (err) {
      toast.error(err, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
  };

  const getWiki = async (products: IProductInfo[]) => {
    if (!products || products.length === 0) {
      setIsLoading(false);
      return;
    }

    await getWikiListing(
      products
        .map((x) => x.productId)
        .filter((id): id is string => Boolean(id)),
      ["Support", "Admin"],
      "Wiki",
      language,
      (wikis) => {
        setSelectedItem(undefined);
        const wikiProducts = (wikis || [])
          .map((x) => ({
            ...x,
            productName:
              products.find((p) => p.productId === x.productId)?.productName ||
              "Unknown Product",
          }))
          .filter((product) => product.productId && product.productName);
        setProducts(wikiProducts);
        // Auto-expand first product for better UX
        if (wikiProducts.length > 0) {
          setExpandedItems([`product-${wikiProducts[0].productId}`]);
        }
        setIsLoading(false);
      },
      (error) => {
        showError(error);
        setIsLoading(false);
      }
    );
  };

  const getProducts = async () => {
    setIsLoading(true);
    const viewAs = viewAsUser || session?.objectId || "";

    await getMyProducts(
      viewAs,
      undefined,
      (response) => {
        const products =
          response.products?.filter((x) => Boolean(x.productId)) || [];
        setProducts(products);
        getWiki(products);
      },
      (error) => {
        showError(error);
        setIsLoading(false);
      }
    );
  };

  const refreshUsersAsync = async () => {
    await getUsersList(
      (response) => {
        const orderedUsers: IUserV0[] = response
          .filter((u) => u.isSupport || u.isAdmin)
          .sort((a, b) => {
            if (a.name < b.name) return -1;
            if (a.name > b.name) return 1;
            return 0;
          });
        setUsers([...orderedUsers]);
      },
      (err) => showError(err)
    );
  };

  useEffect(() => {
    if (session?.loggedIn) {
      getProducts();
    }
  }, [session, viewAsUser]);

  useEffect(() => {
    if (loggedUser.isAdmin) {
      refreshUsersAsync();
    }
  }, [loggedUser]);

  const findPage = (page: IWikiPage, id: string): IWikiPage | undefined => {
    if (!page || !id) {
      return undefined;
    }
    if (page.id === id) {
      return page;
    }
    if (page.pages) {
      let match: IWikiPage | undefined;
      page.pages.forEach((subPage) => {
        if (!match) {
          match = findPage(subPage, id);
          if (match) {
            return;
          }
        }
      });
      return match;
    }
    return undefined;
  };

  const handleTreeViewSelectItem = (
    event: React.SyntheticEvent | null,
    itemId: string,
    isSelected: boolean
  ) => {
    if (!itemId || !isSelected) return;

    setSelectedItem(undefined);
    setSelectedContent(undefined);
    setIsLoadingPage(false);

    if (!products || products.length === 0) {
      return;
    }

    products.forEach((prod) => {
      const match = findPage(prod.root, itemId);
      if (match) {
        setSelectedItem(match);
        const selectedContent = content?.find((c) => c.id === itemId);
        if (!selectedContent && match.hasContent) {
          setIsLoadingPage(true);
          getWikiContent(
            match.id,
            (c) => {
              c.content = c.content?.replace(
                "@WikiDate",
                `Updated on ${c.date}`
              );
              const file = { ...c, id: itemId };
              setContent([...content, file]);
              setSelectedContent(file);
              setIsLoadingPage(false);
            },
            (notFound) => {
              const file = {
                ...notFound,
                content: res.notFoundMessage,
                id: itemId,
              };
              setContent([...content, file]);
              setSelectedContent(file);
              setIsLoadingPage(false);
            },
            () => {
              setIsLoadingPage(false);
            }
          );
        } else {
          setSelectedContent(selectedContent);
        }
        return;
      }
    });
  };

  const handleTreeViewExpandedItemsChange = (
    event: React.SyntheticEvent | null,
    itemId: string,
    isExpanded: boolean
  ) => {
    if (isExpanded) {
      setExpandedItems((prev) => [...prev, itemId]);
    } else {
      setExpandedItems((prev) => prev.filter((id) => id !== itemId));
    }
  };

  // Recursive function to render TreeItems
  const renderTreeItems = (
    page: IWikiPage,
    prod: IWikiProduct,
    level = 0
  ): React.ReactNode => {
    if (!page) return null;

    const hasChildren = page.pages && page.pages.length > 0;
    const iconSize = level === 0 ? "18px" : "16px";
    const textVariant = level === 0 ? "body1" : "body2";

    if (!page || !page.id) {
      return null;
    }
    if (level == 0) {
      page.id = prod.productId;
      page.name = prod.productName || "???";
    }
    return (
      <TreeItem
        key={page.id}
        itemId={page.id}
        label={
          <div
            style={{ display: "flex", alignItems: "center", padding: "2px 0" }}
          >
            <FolderIcon
              style={{
                marginRight: "8px",
                fontSize: iconSize,
                color: hasChildren ? theme.palette.primary.main : "#666",
              }}
            />
            <Typography
              variant={textVariant as "body1" | "body2" | "h6"}
              style={{ fontWeight: level === 0 ? 500 : 400 }}
            >
              {page.name}
            </Typography>
          </div>
        }
      >
        {hasChildren &&
          page.pages.map((childPage) =>
            renderTreeItems(childPage, prod, level + 1)
          )}
      </TreeItem>
    );
  };

  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      root: {
        flexGrow: 1,
        maxWidth: 400,
      },
      backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: "#fff",
      },
      node: {
        padding: "6px",
      },
      header: {
        padding: "0px 6px 0px 32px",
      },
      renderer: {
        padding: "16px 64px 16px 64px",
      },
      viewAsButton: {
        alignSelf: "center",
        margin: "12px 2px 12px 2px",
        maxWidth: "100px",
      },
    })
  );
  const classes = useStyles();

  return (
    <div>
      <ToastContainer />
      <Backdrop className={classes.backdrop} open={isLoading}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Grid container>
        <Grid size={{ xs: 12, sm: 2 }}>
          <>
            <Grid container className={classes.header} direction="column">
              <Typography variant="h3">Wiki Servicios</Typography>
            </Grid>
            {loggedUser?.isAdmin === true && (
              <div>
                <Grid
                  container
                  direction="column"
                  justifyContent="center"
                  alignItems="stretch"
                >
                  <Grid size={{ xs: 12 }}>
                    <UserDropDown
                      name="installationUser"
                      label="User"
                      users={users}
                      value={viewAsUser}
                      enabled={true}
                      allowEmpty={true}
                      allowShowId={true}
                      touched={false}
                      onChange={(e) => setViewAsUser(e.target.value)}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Button
                      onClick={() => getProducts()}
                      className={classes.viewAsButton}
                      type="reset"
                      color="primary"
                      variant="contained"
                    >
                      View As
                    </Button>
                  </Grid>
                </Grid>
                <Divider variant="middle" />
              </div>
            )}
            <Box sx={{ p: 2 }}>
              {products.length > 0 ? (
                <SimpleTreeView
                  onItemSelectionToggle={handleTreeViewSelectItem}
                  onItemExpansionToggle={handleTreeViewExpandedItemsChange}
                  expandedItems={expandedItems}
                  slots={{
                    expandIcon: ChevronRightIcon,
                    collapseIcon: ExpandMoreIcon,
                  }}
                  sx={{
                    flexGrow: 1,
                    maxWidth: 400,
                    // Target all possible TreeItem class names
                    '& [role="treeitem"]': {
                      '& [role="group"]': {
                        paddingLeft: "12px !important",
                        marginLeft: "8px",
                        borderLeft: "1px dashed rgba(0, 0, 0, 0.3)",
                        '& [role="group"]': {
                          paddingLeft: "12px !important",
                          borderLeft: "1px dashed rgba(0, 0, 0, 0.2)",
                        },
                      },
                    },
                    // Also try standard MUI classes
                    "& .MuiTreeItem-content, & .MuiTreeItem2-content": {
                      padding: "4px 8px",
                    },
                    "& .MuiTreeItem-group, & .MuiTreeItem2-group": {
                      marginLeft: "8px !important",
                      paddingLeft: "12px !important",
                      borderLeft: "1px dashed rgba(0, 0, 0, 0.3)",
                    },
                  }}
                >
                  {products
                    .filter(
                      (product) => product.productId && product.productName
                    )
                    .map((product) =>
                      renderTreeItems(product.root, product, 0)
                    )}
                </SimpleTreeView>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {isLoading
                    ? "Loading wiki content..."
                    : "No wiki content available"}
                </Typography>
              )}
            </Box>
          </>
        </Grid>

        <Grid size={{ xs: 12, md: 10 }}>
          <Paper>
            <div className={classes.renderer}>
              {selectedItem && (
                <WikiRenderer
                  title={selectedItem.name}
                  url={selectedItem.url}
                  isLoading={isLoadingPage}
                  date={selectedContent?.date}
                  data={selectedContent?.content}
                />
              )}
            </div>
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
};

export default Wiki;
